import React, { useState } from 'react';
import axios from 'axios';
import { Key, X, Check, Phone, User } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordModal({ open, onClose, lang = 'fr' }) {
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const isAr = lang === 'ar';

  const reset = () => {
    setNom(''); setTelephone(''); setDone(false); setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nom.trim() || !telephone.trim()) {
      setError(isAr ? 'يرجى ملء جميع الحقول' : 'Veuillez remplir tous les champs');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/password-reset-request`, {
        nom: nom.trim(),
        telephone: telephone.trim(),
      });
      if (res.data?.success) setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || (isAr ? 'خطأ' : 'Erreur — réessayez'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const handleClose = () => { reset(); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="forgot-password-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
              <Key size={22} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-zinc-900 text-lg">
                {isAr ? 'نسيت كلمة المرور؟' : 'Mot de passe oublié ?'}
              </h2>
              <p className="text-xs text-zinc-600 mt-0.5">
                {isAr ? 'سيتلقى مدير وكالتك طلبًا بإعادة التعيين' : 'Le chef de votre agence recevra une demande de reset'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-zinc-700 p-1" data-testid="forgot-close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {done ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Check size={32} weight="bold" className="text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-zinc-900 mb-2">
                {isAr ? 'تم إرسال طلبك' : 'Demande envoyée !'}
              </h3>
              <p className="text-sm text-zinc-600 mb-5">
                {isAr
                  ? 'إذا كان هذا الرقم مسجلاً، فسيتلقى مدير وكالتك بريدًا إلكترونيًا لإعادة تعيين كلمة المرور الخاصة بك.'
                  : 'Si ce numéro est enregistré, le chef de votre agence va recevoir un email pour réinitialiser votre mot de passe.'}
              </p>
              <button onClick={handleClose} data-testid="forgot-ok-btn"
                className="px-6 py-2.5 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg font-semibold text-sm">
                {isAr ? 'حسنًا' : "D'accord"}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-zinc-600 leading-relaxed">
                {isAr
                  ? 'املأ اسمك ورقم هاتفك فقط. سيتم إرسال الطلب تلقائيًا إلى البريد الإلكتروني الرسمي لوكالتك.'
                  : 'Saisissez juste votre nom et votre téléphone. La demande sera envoyée automatiquement à l\'email officiel de votre agence.'}
              </p>

              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5 uppercase tracking-wider">
                  {isAr ? 'الاسم الكامل' : 'Nom complet'} *
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input type="text" value={nom} onChange={e => setNom(e.target.value)}
                    data-testid="forgot-nom-input"
                    placeholder={isAr ? 'محمد بن قاسم' : 'Mohamed Benkacem'}
                    className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                    required />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5 uppercase tracking-wider">
                  {isAr ? 'هاتفك' : 'Votre téléphone'} *
                </label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input type="tel" value={telephone} onChange={e => setTelephone(e.target.value)}
                    data-testid="forgot-tel-input"
                    placeholder="+213XXXXXXXXX"
                    className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none font-mono"
                    required />
                </div>
                <p className="text-[11px] text-zinc-500 mt-1">
                  {isAr ? 'الرقم الذي تستخدمه لتسجيل الدخول' : 'Le numéro avec lequel vous vous connectez'}
                </p>
              </div>

              <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-900 flex items-start gap-2">
                <span className="text-base leading-none">📧</span>
                <span>
                  {isAr
                    ? 'سيتلقى مدير وكالتك بريدًا إلكترونيًا تلقائيًا على عنوان الوكالة الرسمي.'
                    : 'Le chef de votre agence recevra automatiquement un email sur l\'adresse officielle de l\'agence.'}
                </span>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={handleClose}
                  className="flex-1 px-4 py-2.5 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 rounded-lg font-semibold text-sm">
                  {isAr ? 'إلغاء' : 'Annuler'}
                </button>
                <button type="submit" disabled={submitting}
                  data-testid="forgot-submit-btn"
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-semibold text-sm shadow-md disabled:opacity-50">
                  {submitting ? (isAr ? 'جاري الإرسال...' : 'Envoi...') : (isAr ? 'إرسال الطلب' : 'Envoyer la demande')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
